import React, { Component } from 'react';
import Axios from 'axios';
import _ from 'lodash';

import { isValidDate, makePresets } from '../../common/helpers';
import { defaultTeethReference as teethReference, isBracketTeethValid as isBracketTeethValidHelper } from '../../common/tx_dx_form_utils';
import { removeEmoji, removeOpposingTeeth, textFieldLimited } from '../../common/functions';
import { handleAnteriorPosteriorCheckboxErrors } from '../../common/case/case_submission';
import { isDateInBetween } from '../../common/date';

import DeForm4 from '../../doctor/de_submission/de_form4';
import ErrorMessage from '../../doctor/components/container/error_message';

import { splitStrToArray } from './edit_tx_de_plan';

/**
 * Displays the DE4.0 plan editing form
 * @component
 * @alias EditDE4Plan
 * @category BPP
 */
class EditDE4Plan extends Component {
  state = {
    ...this.props.treatment_plan,
    de4_teeth_restriction: splitStrToArray(this.props.treatment_plan.de4_teeth_restriction, { keepEmptyStrings: true }),
    de4_bracket_teeth: splitStrToArray(this.props.treatment_plan.de4_bracket_teeth, { keepEmptyStrings: true }),
    de4_missing_teeth: splitStrToArray(this.props.treatment_plan.de4_missing_teeth, { keepEmptyStrings: true }),
    de4_extract_teeth: splitStrToArray(this.props.treatment_plan.de4_extract_teeth, { keepEmptyStrings: true }),
    de4_upper_smartwire_date_warning: false,
    de4_lower_smartwire_date_warning: false,
    de4_anterior_posterior_corrections: this.props.treatment_plan.de4_anterior_posterior_corrections
      ? JSON.parse(this.props.treatment_plan.de4_anterior_posterior_corrections)
      : {},
  };

  componentDidUpdate() {
    if (this.props.save_tx_plan_changes && this.props.treatment_plan) {
      if (this.isDEFormComplete() && this.isDEFormChanged()) {
        this.postTXPlan();
      } else {
        this.props.changeSaveValue();
      }
    }
  }

  /**
   * Performs a POST request to save the form and POST request to send email
   * @function
   */
  postTXPlan() {
    const newTxPlan = {
      description: 'Treatment Plan Edited - ' + this.props.description.trim(),
      ...this.state,
      de4_teeth_restriction: this.state.de4_teeth_restriction.toString(),
      de4_bracket_teeth: removeOpposingTeeth(this.state.de4_arch_to_treat, this.state.de4_bracket_teeth).toString(),
      de4_missing_teeth: this.state.de4_missing_teeth.toString(),
      de4_extract_teeth: this.state.de4_extract_teeth.toString(),
      de4_anterior_posterior_corrections: JSON.stringify(this.state.de4_anterior_posterior_corrections),
      de4_anterior_posterior_notes: this.state.de4_anterior_posterior_notes?.trim() ?? '',
      de4_occlusion_notes: this.state.de4_occlusion_notes?.trim() ?? '',
      de4_additional_instructions: this.state.de4_additional_instructions?.trim() ?? '',
      de4_is_upper_smartwire_new: this.state.de4_is_upper_smartwire_new ? 'true' : 'false',
      de4_is_lower_smartwire_new: this.state.de4_is_lower_smartwire_new ? 'true' : 'false',
    };

    this.props.setStatus({ loading: true, submit: true });
    Axios.post(`/apiV2/treatmentplan/${this.props.case_id}`, newTxPlan)
      .then((resp) => {
        // Axios.post(`/api/email/?slug=treatment-form-change&caseId=${this.props.case_id}&method=standard&provider=${window.location.origin}`);
        this.props.reloadInformation();
        this.props.setStatus({ loading: false });
      })
      .catch((err) => {
        this.props.setStatus({ loading: false, error: true });
      });
  }

  /**
   * Returns whether the de form has changed
   * @function
   * @return {boolean} - True or false
   */
  isDEFormChanged() {
    for (const key in this.state) {
      if (['warning', 'de4_prev_case_info'].includes(key)) {
        continue;
      } else if (key === 'de4_teeth_restriction' || key === 'de4_bracket_teeth' || key === 'de4_missing_teeth' || key === 'de4_extract_teeth') {
        const prev_value = splitStrToArray(this.props.treatment_plan[key]).filter(String);
        const state_value = this.state[key].filter(String);

        if (!(prev_value.every((elem) => state_value.includes(elem)) && state_value.every((elem) => prev_value.includes(elem)))) {
          return true;
        }
      } else if (key === 'de4_anterior_posterior_corrections') {
        if (this.state.de4_anterior_posterior_r === 'correction' || this.state.de4_anterior_posterior_l === 'correction') {
          if (this.props.treatment_plan[key].replaceAll(' ', '') !== JSON.stringify(this.state[key]).replaceAll(' ', '')) return true;
        }
      } else if (['de4_upper_smartwire_date', 'de4_lower_smartwire_date'].includes(key)) {
        let treatmentPlanDate = this.props.treatment_plan[key] ? this.props.treatment_plan[key] : '';
        let stateDate = this.state[key] ? this.state[key] : '';
        if (treatmentPlanDate !== stateDate) return true;
      } else if (key === 'de4_anterior_posterior_notes') {
        if (this.state.de4_anterior_posterior_r === 'other' || this.state.de4_anterior_posterior_l === 'other') {
          if (this.props.treatment_plan[key] !== this.state[key]) return true;
        }
      } else if (key === 'de4_occlusion_notes') {
        if (this.state.de4_occlusion === 'No') {
          if (this.props.treatment_plan[key] !== this.state[key]) return true;
        }
      } else {
        if (this.props.treatment_plan[key] !== this.state[key] && !key.includes('date_warning')) return true;
      }
    }

    const warning = '<li>No changes have been made. Please update the Treatment Plan form to save.</li>';
    this.props.showWarning(warning);

    return false;
  }

  /**
   * Returns whether de form's required fields are completed
   * @function
   * @return {boolean} - True or false
   */
  isDEFormComplete() {
    const is_complete =
      this.props.description.trim() !== '' &&
      this.state.de4_arch_to_treat &&
      !this.state.de4_upper_smartwire_date_warning &&
      !this.state.de4_lower_smartwire_date_warning &&
      this.isBracketTeethValid() &&
      this.isAnteriorPosteriorFieldValid() &&
      this.isAnteriorPosteriorNotesFieldValid() &&
      this.isOcclusionNotesValid();

    if (!is_complete) {
      let errors = '';
      if (this.props.description.trim() === '') {
        if (document.querySelector('#tx_edit_desrcription_title')) {
          document.querySelector('#tx_edit_desrcription_title').classList.add('warning-text');
        }
        if (document.querySelector('#tx_edit_desrcription_heading')) {
          document.querySelector('#tx_edit_desrcription_heading').classList.add('warning-text');
        }
        if (document.querySelector('#tx_edit_desrcription_textarea')) {
          document.querySelector('#tx_edit_desrcription_textarea').classList.add('warning-border');
        }
        errors = errors + '<li><a href="#tx_edit_desrcription_heading">Description of Changes</a></li>';
      }

      if (!this.state.de4_arch_to_treat) {
        document.querySelector('#de4_arch_to_treat').classList.add('warning-text');
        errors = errors + '<li><a href="#de4_arch_to_treat">Arch to Treat</a></li>';
      }

      if (!this.isBracketTeethValid()) {
        document.querySelector('#de4_bracket_teeth').classList.add('warning-text');
        errors = errors + '<li><a href="#de4_bracket_teeth">Bracket Teeth</a></li>';
      }

      if (!this.isAnteriorPosteriorFieldValid() || !this.isAnteriorPosteriorNotesFieldValid()) {
        document.querySelector('#de4_anterior_posterior').classList.add('warning-text');
        if (!this.isAnteriorPosteriorFieldValid()) {
          handleAnteriorPosteriorCheckboxErrors(true);
        }
        if (!this.isAnteriorPosteriorNotesFieldValid()) {
          if (document.querySelector('textarea[data-field="de4_anterior_posterior_notes"]')) {
            document.querySelector('textarea[data-field="de4_anterior_posterior_notes"]').classList.add('warning-border');
          }
        }
        errors = errors + '<li><a href="#de4_anterior_posterior">Anterior-Posterior</a></li>';
      }

      if (!this.isOcclusionNotesValid()) {
        document.querySelector('#de4_occlusion').classList.add('warning-text');
        if (document.querySelector('textarea[data-field="de4_occlusion_notes"]')) {
          document.querySelector('textarea[data-field="de4_occlusion_notes"]').classList.add('warning-border');
        }
        errors = errors + '<li><a href="#de4_anterior_posterior">Occlusion Notes</a></li>';
      }

      this.props.showWarning(errors);
    }

    return is_complete;
  }

  /**
   * Determines if anterior-posterior field is valid
   * @function
   * @returns {boolean} - True or false
   */
  isAnteriorPosteriorFieldValid = () => {
    const correctionIsSelected = [this.state.de4_anterior_posterior_l, this.state.de4_anterior_posterior_r].includes('correction');
    if (correctionIsSelected) {
      return Object.values(this.state.de4_anterior_posterior_corrections).some((value) => value);
    }
    return true;
  };

  /**
   * Determines if anterior-posterior notes field is valid
   * @function
   * @returns {boolean} - True or false
   */
  isAnteriorPosteriorNotesFieldValid = () => {
    const otherIsSelected = [this.state.de4_anterior_posterior_l, this.state.de4_anterior_posterior_r].includes('other');
    if (otherIsSelected) {
      return this.state.de4_anterior_posterior_notes && this.state.de4_anterior_posterior_notes.trim() !== '';
    }
    return true;
  };

  isOcclusionNotesValid = () => {
    return this.state.de4_occlusion !== 'No' || this.state.de4_occlusion_notes !== '';
  };

  /**
   * Checks if DE4.0 bracketTeeth is valid based on selected missingTeeth, extractTeeth and archToTreat
   * @param {object} data object with bracketTeeth, missingTeeth, extractTeeth, and archToTreat
   * @returns {boolean} - True or false
   */
  isBracketTeethValid = ({ bracketTeeth, missingTeeth, extractTeeth, archToTreat } = {}) => {
    const currentBracketTeeth = bracketTeeth || this.state.de4_bracket_teeth;
    const currentMissingTeeth = missingTeeth || this.state.de4_missing_teeth;
    const currentExtractTeeth = extractTeeth || this.state.de4_extract_teeth;
    const currentArchToTreat = archToTreat || this.state.de4_arch_to_treat;

    return isBracketTeethValidHelper({
      bracketTeeth: currentBracketTeeth,
      missingTeeth: currentMissingTeeth,
      extractTeeth: currentExtractTeeth,
      archToTreat: currentArchToTreat,
    });
  };

  /**
   * Handles all input changes on the form
   * @function
   * @param {object} event - The event object on change
   */
  onInputChange_de4 = (event) => {
    let de4_upper_smartwire = this.state.de4_upper_smartwire;
    let de4_upper_smartwire_date = this.state.de4_upper_smartwire_date;
    let de4_lower_smartwire = this.state.de4_lower_smartwire;
    let de4_lower_smartwire_date = this.state.de4_lower_smartwire_date;
    let de4_arch_to_treat = this.state.de4_arch_to_treat;
    let de4_teeth_restriction = this.state.de4_teeth_restriction;
    let de4_bracket_teeth = this.state.de4_bracket_teeth;
    let de4_missing_teeth = this.state.de4_missing_teeth;
    let de4_extract_teeth = this.state.de4_extract_teeth;
    let de4_upper_smartwire_date_warning = this.state.de4_upper_smartwire_date_warning;
    let de4_lower_smartwire_date_warning = this.state.de4_lower_smartwire_date_warning;
    let de4_anterior_posterior_r = this.state.de4_anterior_posterior_r;
    let de4_anterior_posterior_l = this.state.de4_anterior_posterior_l;
    let de4_anterior_posterior_corrections = this.state.de4_anterior_posterior_corrections;
    let de4_anterior_posterior_notes = this.state.de4_anterior_posterior_notes;
    let de4_occlusion = this.state.de4_occlusion;
    let de4_occlusion_notes = this.state.de4_occlusion_notes;
    let de4_additional_instructions = this.state.de4_additional_instructions;

    const event_type = event?.target?.dataset?.field ?? event?.field ?? '';

    switch (event_type) {
      case 'de4_arch_to_treat':
        const opposingArch = event.target.value === 'upper' ? 'lower' : 'upper';
        if (de4_arch_to_treat === event.target.value) de4_arch_to_treat = '';
        else if (de4_arch_to_treat === 'both') de4_arch_to_treat = opposingArch;
        else if (!de4_arch_to_treat) de4_arch_to_treat = event.target.value;
        else de4_arch_to_treat = 'both';
        break;

      case 'de4_upper_smartwire':
        de4_upper_smartwire = event.value;
        if (de4_upper_smartwire === 'none') {
          de4_upper_smartwire_date = '';
        }
        break;

      case 'de4_upper_smartwire_date':
        de4_upper_smartwire_date = event.target.value;
        de4_upper_smartwire_date_warning = !isValidDate(de4_upper_smartwire_date);
        break;

      case 'de4_lower_smartwire':
        de4_lower_smartwire = event.value;

        if (de4_lower_smartwire === 'none') {
          de4_lower_smartwire_date = '';
        }
        break;

      case 'de4_lower_smartwire_date':
        de4_lower_smartwire_date = event.target.value;
        de4_lower_smartwire_date_warning = !isValidDate(de4_lower_smartwire_date);
        break;

      case 'de4_arch_to_treat':
        de4_arch_to_treat = event.target.value;
        break;

      case 'de4_occlusion':
        de4_occlusion = event.target.value;
        break;

      case 'de4_occlusion_notes':
        de4_occlusion_notes = removeEmoji(textFieldLimited(event.target.value));
        break;

      case 'de4_additional_instructions':
        de4_additional_instructions = removeEmoji(textFieldLimited(event.target.value));
        break;

      case 'de4_teeth_restriction':
        if (_.intersection(de4_teeth_restriction, [event.target.value]).length > 0) {
          de4_teeth_restriction = _.difference(de4_teeth_restriction, [event.target.value]);
        } else {
          de4_teeth_restriction.push(event.target.value);
        }
        break;

      case 'de4_bracket_teeth':
        if (_.intersection(de4_bracket_teeth, [event.target.value]).length > 0) {
          de4_bracket_teeth = _.difference(de4_bracket_teeth, [event.target.value]);
        } else {
          de4_bracket_teeth.push(event.target.value);
        }
        this.props.clearWarning();
        break;

      case 'de4_missing_teeth':
        if (_.intersection(de4_missing_teeth, [event.target.value]).length > 0) {
          de4_missing_teeth = _.difference(de4_missing_teeth, [event.target.value]);
        } else {
          de4_missing_teeth.push(event.target.value);
        }
        break;

      case 'de4_extract_teeth':
        if (_.intersection(de4_extract_teeth, [event.target.value]).length > 0) {
          de4_extract_teeth = _.difference(de4_extract_teeth, [event.target.value]);
        } else {
          de4_extract_teeth.push(event.target.value);
        }
        break;

      case 'de4_anterior_posterior_r':
        de4_anterior_posterior_r = event.target.value;
        break;

      case 'de4_anterior_posterior_l':
        de4_anterior_posterior_l = event.target.value;
        break;

      case 'de4_anterior_posterior_corrections':
        const { name, checked } = event.target;
        de4_anterior_posterior_corrections = {
          ...de4_anterior_posterior_corrections,
          [name]: checked,
        };
        break;

      case 'de4_anterior_posterior_notes':
        de4_anterior_posterior_notes = removeEmoji(textFieldLimited(event.target.value));
        break;

      default:
    }

    this.props.clearWarning();

    this.setState({
      de4_upper_smartwire: de4_upper_smartwire,
      de4_upper_smartwire_date: de4_upper_smartwire_date,
      de4_lower_smartwire: de4_lower_smartwire,
      de4_lower_smartwire_date: de4_lower_smartwire_date,
      de4_arch_to_treat: de4_arch_to_treat,
      de4_teeth_restriction: de4_teeth_restriction,
      de4_bracket_teeth: de4_bracket_teeth,
      de4_missing_teeth: de4_missing_teeth,
      de4_extract_teeth: de4_extract_teeth,
      de4_upper_smartwire_date_warning: de4_upper_smartwire_date_warning,
      de4_lower_smartwire_date_warning: de4_lower_smartwire_date_warning,
      de4_anterior_posterior_r: de4_anterior_posterior_r,
      de4_anterior_posterior_l: de4_anterior_posterior_l,
      de4_anterior_posterior_corrections: de4_anterior_posterior_corrections,
      de4_anterior_posterior_notes: de4_anterior_posterior_notes,
      de4_occlusion: de4_occlusion,
      de4_occlusion_notes: de4_occlusion_notes,
      de4_additional_instructions: de4_additional_instructions,
    });
  };

  /**
   * Retrieves teeth sets for DE
   * @function
   * @returns {object} - Teeth sets
   */
  getDE4ImpliedTeethSets = () => {
    const opposingTeeth = [];
    const hasUpper = this.de4HasUpper();
    const hasLower = this.de4HasLower();
    const setupCurrent = this.state.de4_arch_to_treat !== 'both';

    if (!hasUpper) {
      opposingTeeth.push(...teethReference.UR);
      opposingTeeth.push(...teethReference.UL);
    }

    if (!hasLower) {
      opposingTeeth.push(...teethReference.LR);
      opposingTeeth.push(...teethReference.LL);
    }

    const missingTeeth = this.state.de4_missing_teeth;
    const extractTeeth = _.difference(this.state.de4_extract_teeth, missingTeeth);
    const extractDisabled = missingTeeth;
    const extractTeethAll = _.union(extractTeeth, missingTeeth);

    let bracketDisabled = extractTeethAll;
    if (setupCurrent) bracketDisabled = _.union(bracketDisabled, opposingTeeth);
    const bracketTeeth = _.difference(this.state.de4_bracket_teeth, bracketDisabled);

    let restrictImplied = _.difference(teethReference.All, bracketTeeth);
    restrictImplied = _.union(restrictImplied, extractTeethAll);
    let restrictTeeth = _.union(this.state.de4_teeth_restriction, restrictImplied);
    if (setupCurrent) restrictTeeth = _.union(restrictTeeth, opposingTeeth);

    return {
      hasUpper,
      hasLower,
      setupCurrent,
      missingTeeth,
      extractTeeth,
      extractDisabled,
      extractTeethAll,
      opposingTeeth,
      bracketTeeth,
      bracketDisabled,
      restrictTeeth,
      restrictImplied,
    };
  };

  /**
   * Determines if DE has lower arch
   * @function
   * @returns {boolean} - True or false
   */
  de4HasLower = () => {
    return this.state.de4_arch_to_treat === 'both' || this.state.de4_arch_to_treat === 'lower';
  };

  /**
   * Determines if DE has upper arch
   * @function
   * @returns {boolean} - True or false
   */
  de4HasUpper = () => {
    return this.state.de4_arch_to_treat === 'both' || this.state.de4_arch_to_treat === 'upper';
  };

  /**
   * Sets bracket teeth
   * @function
   * @param {list} teeth - Bracket teeth
   */
  setBracketTeeth = (teeth) => {
    const bracket_teeth =
      teeth !== undefined
        ? removeOpposingTeeth(this.state.de4_arch_to_treat, teeth)
        : removeOpposingTeeth(this.state.de4_arch_to_treat, this.state.de4_bracket_teeth);
    this.setState({
      de4_bracket_teeth: bracket_teeth,
    });
  };

  getPrevBrackets = () => {
    let prevCaseInfo = this.state.de4_prev_case_info;
    if (prevCaseInfo) {
      prevCaseInfo = JSON.parse(prevCaseInfo);
      return prevCaseInfo.bracket_teeth;
    }
    return [];
  };

  render() {
    return (
      <>
        <DeForm4
          onInputChange_de4={this.onInputChange_de4}
          onFocus={this.props.clearWarning}
          getDeHold={() => {}}
          photoUpload={this.props.photoUpload}
          getImpliedTeethSets={this.getDE4ImpliedTeethSets}
          hasUpper={this.de4HasUpper}
          hasLower={this.de4HasLower}
          teethReference={teethReference}
          gen_2={this.props.gen_2}
          minPreset={4}
          maxPreset={7}
          presets={makePresets(4, 7)}
          setBracketTeeth={this.setBracketTeeth}
          {...this.state}
          isEdit={true}
          prevWireSequences={this.props.prevWireSequences}
          prevArches={this.props.prevArches}
          prevBrackets={this.getPrevBrackets()}
        />
        <ErrorMessage
          title="Incomplete Fields"
          onClose={this.props.clearWarning}
          className={this.props.warning ? 'error-message-container' : 'error-message-container hide'}
        >
          <div id="warning-submit">Warning</div>
        </ErrorMessage>
      </>
    );
  }
}

export default EditDE4Plan;
